import {
    ArrowPathIcon,
    ArrowUturnLeftIcon,
    PaperAirplaneIcon,
    PlayCircleIcon,
    StopCircleIcon
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { ActionCategoryCard } from './ActionCategoryCard';
import { ActionTile } from './ActionTile';
import {
    canEndTreatment,
    canReactivateCase,
    canTriggerRefinement,
    showsOrthoDecidedActions,
    showsReactivateCase
} from './actionAvailability';
import { AppActivationPill, MonitoringStatusPill } from '@odo/shared-ui';
import { ManageMonitoringModal } from './ManageMonitoringModal';
import type { MonitoringAction, MonitoringActionOptions } from './monitoringStatus';
import { StartMonitoringPatient } from '../../../services/patient';

const DM_NOT_LINKED_REASON = 'Patient must be created in Dental Monitoring first.';

interface PatientActionsTabProps {
    monitoringStatus?: string | null;
    dmAppActivationStatus?: string | null;
    /** Null/undefined means the patient has no Dental Monitoring account yet. */
    dmId?: number | null;
    /** Drives client-side gating of the Treatment actions below (see actionAvailability.ts). */
    internalCaseStatus?: string | null;
    version?: string | null;
    /** Patient identity threaded to ManageMonitoringModal's 'start' step. */
    monitoringPatient: StartMonitoringPatient | null;
    onMonitoringAction?: (
        action: MonitoringAction,
        options?: MonitoringActionOptions
    ) => Promise<boolean>;
    onSendDMActivationLink?: () => void;
    isMonitoringActionLoading?: boolean;
    isSendLinkLoading?: boolean;
    onEndTreatment?: () => void;
    isEndTreatmentLoading?: boolean;
    onTriggerRefinement?: () => void;
    isTriggerRefinementLoading?: boolean;
    onReactivateCase?: () => void;
    isReactivateCaseLoading?: boolean;
}

function PatientActionsTab({
    monitoringStatus,
    dmAppActivationStatus,
    dmId,
    internalCaseStatus,
    version,
    monitoringPatient,
    onMonitoringAction,
    onSendDMActivationLink,
    isMonitoringActionLoading = false,
    isSendLinkLoading = false,
    onEndTreatment,
    isEndTreatmentLoading = false,
    onTriggerRefinement,
    isTriggerRefinementLoading = false,
    onReactivateCase,
    isReactivateCaseLoading = false
}: PatientActionsTabProps) {
    const [isManageMonitoringOpen, setIsManageMonitoringOpen] = useState(false);

    const endTreatmentAvailability = canEndTreatment({ internalCaseStatus });
    const triggerRefinementAvailability = canTriggerRefinement({ internalCaseStatus });
    const reactivateCaseAvailability = canReactivateCase({ internalCaseStatus });
    const isReactivateCaseVisible = showsReactivateCase({ internalCaseStatus });
    const areOrthoDecidedActionsVisible = showsOrthoDecidedActions({ version });

    // Activation link shares the monitoring actions' disabled/loading gate.
    const isSendLinkTileLoading = isSendLinkLoading || isMonitoringActionLoading;

    // An unlinked patient can carry a stale monitoring/app-activation status.
    const isDmLinked = dmId != null;
    const hasDmStatus = isDmLinked && (Boolean(monitoringStatus) || Boolean(dmAppActivationStatus));

    return (
        <div className="flex-1 min-h-0 overflow-auto p-6">
            <div className="flex flex-col gap-3">
                <ActionCategoryCard
                    title="Dental Monitoring"
                    headerExtra={
                        hasDmStatus ? (
                            <>
                                <MonitoringStatusPill status={monitoringStatus} />
                                <AppActivationPill status={dmAppActivationStatus} />
                            </>
                        ) : undefined
                    }
                >
                    {!isDmLinked && (
                        <p className="w-full text-xs text-gray-400">
                            This section will be available once the patient is created in Dental
                            Monitoring.
                        </p>
                    )}
                    <ActionTile
                        icon={PlayCircleIcon}
                        label="Manage monitoring"
                        onSelect={() => setIsManageMonitoringOpen(true)}
                        disabled={!isDmLinked}
                        disabledReason={!isDmLinked ? DM_NOT_LINKED_REASON : undefined}
                    />
                    <ActionTile
                        icon={PaperAirplaneIcon}
                        label="Send DM app activation link"
                        onSelect={() => onSendDMActivationLink?.()}
                        loading={isSendLinkTileLoading}
                        disabled={!isDmLinked || isSendLinkTileLoading}
                        disabledReason={!isDmLinked ? DM_NOT_LINKED_REASON : undefined}
                    />
                </ActionCategoryCard>

                {(areOrthoDecidedActionsVisible || isReactivateCaseVisible) && (
                    <ActionCategoryCard title="Treatment">
                        {onTriggerRefinement && areOrthoDecidedActionsVisible && (
                            <ActionTile
                                icon={ArrowPathIcon}
                                label="Trigger refinement"
                                onSelect={onTriggerRefinement}
                                loading={isTriggerRefinementLoading}
                                disabled={
                                    isTriggerRefinementLoading ||
                                    !triggerRefinementAvailability.allowed
                                }
                                disabledReason={triggerRefinementAvailability.reason}
                            />
                        )}
                        {onEndTreatment && areOrthoDecidedActionsVisible && (
                            <ActionTile
                                icon={StopCircleIcon}
                                label="End treatment"
                                onSelect={onEndTreatment}
                                loading={isEndTreatmentLoading}
                                disabled={
                                    isEndTreatmentLoading || !endTreatmentAvailability.allowed
                                }
                                disabledReason={endTreatmentAvailability.reason}
                            />
                        )}
                        {onReactivateCase && isReactivateCaseVisible && (
                            <ActionTile
                                icon={ArrowUturnLeftIcon}
                                label="Reactivate case"
                                onSelect={onReactivateCase}
                                loading={isReactivateCaseLoading}
                                disabled={
                                    isReactivateCaseLoading || !reactivateCaseAvailability.allowed
                                }
                                disabledReason={reactivateCaseAvailability.reason}
                            />
                        )}
                    </ActionCategoryCard>
                )}
            </div>

            <ManageMonitoringModal
                isOpen={isManageMonitoringOpen}
                onClose={() => setIsManageMonitoringOpen(false)}
                monitoringStatus={monitoringStatus}
                patient={monitoringPatient}
                onAction={(action, options) =>
                    onMonitoringAction
                        ? onMonitoringAction(action, options)
                        : Promise.resolve(false)
                }
                isLoading={isMonitoringActionLoading}
            />
        </div>
    );
}

export { PatientActionsTab };
